import { useDictionary } from '../../../../utilities/hooks/UseDictionary';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import React from 'react';
import { getProviderGroups } from '../provider_experiences/networking';
import empty from '../../../../assets/images/empty_bg.svg';
import addSquare from '../../../../assets/icons/Plus.svg';
import minusSquare from '../../../../assets/icons/Minus.svg';

import ImageHelper from '../../../../utilities/helpers/ImageHelper';
import FormLoader from '../../common/FormLoader';
import ChannelIcon from '../../common/ChannelIcon';
import { useBookingChannels } from '../../../../utilities/hooks/useBookingChannels';

const CHANNEL_WEBSITE = 2;
const CHANNEL_OFFLINE = 4;

const AddGroupDialog = ({ actionType, experience, setAddGroupDialogVisibility, addGroup, removeGroup, createNewGroup }) => {

    const [dialogOpacity, setDialogOpacity] = useState(0);
    const dictionary = useDictionary();
    const bookingChannels = useBookingChannels();
    const [showPopupLoader, setShowPopupLoader] = useState(false);
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(0);
    const [sortOrder, setSortOrder] = useState(null);
    const [groups, setGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [pendingChannelGroupId, setPendingChannelGroupId] = useState(null);
    const [pendingChannel, setPendingChannel] = useState(CHANNEL_WEBSITE);

    const hasWebsite = !!experience?.channels?.website;
    const hasOffline = !!experience?.channels?.offline;
    const needsChannelPick = hasWebsite && hasOffline;

    useEffect(() => {
        let options = { value: "created_at", direction: "desc" };
        setSortOrder(options);
        setTimeout(() => {
            setDialogOpacity(1);
        }, 50);
    }, []);

    useEffect(() => {
        getGroups();
    }, [sortOrder, pageSize, currentPage]);

    const getGroups = async () => {
        setShowPopupLoader(true);
        try {
            if(!experience.id){
                experience.id = experience.experience_id;
            }
            const excludeExperienceIds = [experience.id];
            let { data } = await getProviderGroups(
                sortOrder,
                pageSize,
                currentPage,
                excludeExperienceIds
            );
            if (data) {
                setGroups(data.data);
                setShowPopupLoader(false);
                const initialSelectedGroups = data.data
                    .filter(group => group.experiences.some(exp => exp.id === experience.id))
                    .map(group => group.id);
                setSelectedGroups(initialSelectedGroups);
            }

        } catch (e) {
            setShowPopupLoader(false);
        }
    };

    const onCancelClicked = () => {
        setDialogOpacity(0);
        setTimeout(() => {
            setAddGroupDialogVisibility(false);
        }, 200);
    };

    const performAdd = async (groupId, channelId) => {
        setShowPopupLoader(true);
        try {
            await addGroup(groupId, channelId);
            setSelectedGroups((prev) => [...prev, groupId]);
            await getGroups();
        } catch (error) {
            console.error('Error adding to group:', error);
        } finally {
            setShowPopupLoader(false);
        }
    };

    const performRemove = async (groupId) => {
        setShowPopupLoader(true);
        try {
            await removeGroup(groupId);
            setSelectedGroups((prev) => prev.filter((id) => id !== groupId));
            setGroups((prevGroups) =>
                prevGroups.map((group) =>
                    group.id === groupId
                        ? { ...group, experiences: group.experiences.filter((exp) => exp.id !== experience.id) }
                        : group
                )
            );
        } catch (error) {
            console.error('Error removing from group:', error);
        } finally {
            setShowPopupLoader(false);
        }
    };

    const handleGroupClick = (groupId) => {
        if (selectedGroups.includes(groupId)) {
            performRemove(groupId);
            return;
        }
        if (needsChannelPick) {
            setPendingChannel(CHANNEL_WEBSITE);
            setPendingChannelGroupId(groupId);
            return;
        }
        const channelId = hasOffline ? CHANNEL_OFFLINE : CHANNEL_WEBSITE;
        performAdd(groupId, channelId);
    };

    const confirmChannelPick = () => {
        const groupId = pendingChannelGroupId;
        const channelId = pendingChannel;
        setPendingChannelGroupId(null);
        performAdd(groupId, channelId);
    };

    const cancelChannelPick = () => {
        setPendingChannelGroupId(null);
    };

    return (
        <div className="dialog-wrapper" style={{ opacity: dialogOpacity }}>
            <div className="group-dialog-container">
                <FormLoader showLoader={showPopupLoader} />
                <div className="group-dialog-content-container">
                    <div className="group-dialog-header">
                        <div></div>
                        <h3
                            style={{ fontStyle: 'normal'}}
                            className="group-dialog-title"
                        >
                            {dictionary.manage_collections}
                        </h3>
                        <div
                            onClick={onCancelClicked}
                            className="dialog-close-button"
                            style={{ position: 'unset'}}
                        />
                    </div>
                    <div className="flex-filler" />
                    <div className="groups-selection-container">
                        <div className="popup-scroll">
                            {groups.map((group) => {
                                const matchingExperience = group.experiences.find(exp => exp.id === experience.id);
                                const isSelected = !!matchingExperience;
                                const selectedChannelId = matchingExperience?.channel_id;
                                const isPending = pendingChannelGroupId === group.id;
                                return (
                                <div key={group.id} style={{ display: 'flex', flexDirection: 'column', marginTop: 15, padding: '8px', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div className="group-details-dialog-row items-center">
                                            <img
                                                className="group-image"
                                                onError={(e) => {
                                                    e.currentTarget.src = empty;
                                                }}
                                                src={
                                                    group.experiences.length > 0
                                                        ? ImageHelper.getFullImageUrl(
                                                            group.experiences[group?.experiences.length - 1].thumbnail_url
                                                        )
                                                        : empty
                                                }
                                            />
                                            <div className="group-info">
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                    <span className='group-info-details-title'>{group.title}</span>
                                                    {isSelected && needsChannelPick && selectedChannelId ? (
                                                        <span
                                                            data-testid={`group-channel-${group.id}`}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, opacity: 0.7 }}
                                                        >
                                                            <ChannelIcon channelId={selectedChannelId} size={12} style={{ width: 12, height: 12, borderRadius: 0 }} />
                                                            {bookingChannels[Number(selectedChannelId)]?.text}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div className='group-info-details-description'>{group.experiences.length} item</div>
                                            </div>
                                        </div>
                                        <div
                                            className={`group-status-image-wrapper${isSelected ? ' group-status-image-minus' : ''}`}
                                            onClick={() => handleGroupClick(group.id)}
                                            data-testid={`group-add-${group.id}`}
                                        >
                                            <img
                                                style={{ borderRadius: '50%' }}
                                                className={`group-status-image`}
                                                src={isSelected ? minusSquare : addSquare}
                                            />
                                        </div>
                                    </div>
                                    {isPending ? (
                                        <div
                                            className="channel-pick-popover"
                                            data-testid="channel-pick-popover"
                                        >
                                            <div className="channel-pick-title">
                                                {dictionary.add_to_collection_choose_channel}
                                            </div>
                                            <div className="channel-pick-options">
                                                <label className="channel-pick-option">
                                                    <input
                                                        type="radio"
                                                        className="channel-pick-input"
                                                        name={`channel-${group.id}`}
                                                        checked={pendingChannel === CHANNEL_WEBSITE}
                                                        onChange={() => setPendingChannel(CHANNEL_WEBSITE)}
                                                        data-testid="channel-pick-option-website"
                                                    />
                                                    <span className="channel-pick-radio" aria-hidden="true" />
                                                    <ChannelIcon channelId={CHANNEL_WEBSITE} />
                                                    <span className="channel-pick-label">
                                                        {dictionary.website}
                                                    </span>
                                                </label>
                                                <label className="channel-pick-option">
                                                    <input
                                                        type="radio"
                                                        className="channel-pick-input"
                                                        name={`channel-${group.id}`}
                                                        checked={pendingChannel === CHANNEL_OFFLINE}
                                                        onChange={() => setPendingChannel(CHANNEL_OFFLINE)}
                                                        data-testid="channel-pick-option-offline"
                                                    />
                                                    <span className="channel-pick-radio" aria-hidden="true" />
                                                    <ChannelIcon channelId={CHANNEL_OFFLINE} />
                                                    <span className="channel-pick-label">
                                                        {dictionary.offline}
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="channel-pick-actions">
                                                <button
                                                    type="button"
                                                    className="secondary-button"
                                                    onClick={cancelChannelPick}
                                                    data-testid="channel-pick-cancel"
                                                >
                                                    {dictionary.cancel}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="button"
                                                    onClick={confirmChannelPick}
                                                    data-testid="channel-pick-confirm"
                                                >
                                                    {dictionary.confirm}
                                                </button>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex-filler" />
                    <div className='create-new-groupCol'>
                        <button
                            type="button"
                            key="create-new-group"
                            className="create-new-group-button"
                            onClick={() => createNewGroup()}
                            data-testid="create-new-collection-button"
                        >
                            <img className="create-new-group-icon" src={addSquare} alt="" />
                            <span className="create-new-group-label">{dictionary.create_new_group}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

AddGroupDialog.propTypes = {
    actionType: PropTypes.string.isRequired,
    experience: PropTypes.object.isRequired,
    setAddGroupDialogVisibility: PropTypes.func,
    addGroup: PropTypes.func,
    removeGroup: PropTypes.func,
    createNewGroup: PropTypes.func,
};

export default AddGroupDialog;
